import { useState, useEffect } from 'react';
import { X, ArrowRightLeft, Package, MapPin, AlertTriangle } from 'lucide-react';
import { inventoryApi, type ProductResponse, type LocationResponse } from '../../api/inventoryApi';
import SearchableSelect from '../../components/ui/SearchableSelect';

interface TransferModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    salonId: string;
    products: ProductResponse[];
    locations: LocationResponse[];
    preselectedProduct?: ProductResponse | null;
}

const TransferModal = ({ isOpen, onClose, onSuccess, salonId, products, locations, preselectedProduct }: TransferModalProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [step, setStep] = useState(1); // 1: Select, 2: Confirm

    // Form state
    const [selectedProductId, setSelectedProductId] = useState('');
    const [fromLocationId, setFromLocationId] = useState('');
    const [toLocationId, setToLocationId] = useState('');
    const [quantity, setQuantity] = useState('');
    const [notes, setNotes] = useState('');

    // Computed values
    const selectedProduct = products.find(p => p.id === selectedProductId);
    const fromLocation = locations.find(l => l.id === fromLocationId);
    const toLocation = locations.find(l => l.id === toLocationId);
    const availableStock = selectedProduct?.stockByLocation.find(s => s.locationId === fromLocationId)?.quantity ?? 0;

    // Initialize with preselected product
    useEffect(() => {
        if (preselectedProduct) {
            setSelectedProductId(preselectedProduct.id);
            // Default to first location with stock
            const locationWithStock = preselectedProduct.stockByLocation.find(s => s.quantity > 0);
            if (locationWithStock) {
                setFromLocationId(locationWithStock.locationId);
            }
        }
    }, [preselectedProduct]);

    // Reset on close
    useEffect(() => {
        if (!isOpen) {
            setStep(1);
            if (!preselectedProduct) {
                setSelectedProductId('');
            }
            setFromLocationId('');
            setToLocationId('');
            setQuantity('');
            setNotes('');
            setError(null);
        }
    }, [isOpen, preselectedProduct]);

    // Get available "from" locations (only those with stock)
    const fromLocations = selectedProduct?.stockByLocation.filter(s => s.quantity > 0) ?? [];

    // Get available "to" locations (exclude selected from location)
    const toLocations = locations.filter(l => l.id !== fromLocationId);

    const handleNext = () => {
        if (!selectedProductId || !fromLocationId || !toLocationId || !quantity) {
            setError('Por favor completa todos los campos');
            return;
        }

        const qty = parseInt(quantity);
        if (qty <= 0) {
            setError('La cantidad debe ser mayor a 0');
            return;
        }
        if (qty > availableStock) {
            setError(`Stock insuficiente. Disponible: ${availableStock}`);
            return;
        }

        setError(null);
        setStep(2);
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        setError(null);

        try {
            await inventoryApi.transferStock(salonId, {
                productId: selectedProductId,
                fromLocationId,
                toLocationId,
                quantity: parseInt(quantity),
                notes: notes || undefined,
            });

            onSuccess();
            onClose();
        } catch (err: any) {
            console.error('Error transferring stock:', err);
            setError(err.response?.data?.message || 'Error al transferir el stock');
            setStep(1);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="bg-gradient-to-r from-green-600 to-teal-600 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                            <ArrowRightLeft className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-white">
                                Transferir Stock
                            </h2>
                            <p className="text-green-100 text-sm">
                                Mover productos entre ubicaciones
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-white" />
                    </button>
                </div>

                {/* Progress Steps */}
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
                    <div className="flex items-center justify-center gap-4">
                        <div className={`flex items-center gap-2 ${step >= 1 ? 'text-green-600' : 'text-gray-400'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'
                                }`}>1</div>
                            <span className="text-sm font-medium">Seleccionar</span>
                        </div>
                        <div className={`w-16 h-1 rounded-full ${step >= 2 ? 'bg-green-600' : 'bg-gray-200'}`}></div>
                        <div className={`flex items-center gap-2 ${step >= 2 ? 'text-green-600' : 'text-gray-400'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'
                                }`}>2</div>
                            <span className="text-sm font-medium">Confirmar</span>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                            {error}
                        </div>
                    )}

                    {step === 1 && (
                        <div className="space-y-5">
                            {/* Product Selection */}
                            <SearchableSelect
                                label="Producto a Transferir"
                                placeholder="Selecciona un producto..."
                                options={products.filter(p => p.totalStock > 0).map(product => ({
                                    value: product.id,
                                    label: `${product.name} (${product.totalStock} ${product.unit} en total)`
                                }))}
                                value={selectedProductId}
                                onChange={val => {
                                    setSelectedProductId(val);
                                    setFromLocationId('');
                                    setToLocationId('');
                                }}
                            />

                            {selectedProduct && (
                                <>
                                    {/* From Location */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            <MapPin className="w-4 h-4 inline mr-1 text-red-500" />
                                            Desde (Origen)
                                        </label>
                                        {fromLocations.length === 0 ? (
                                            <p className="text-gray-500 text-sm p-3 bg-gray-50 rounded-lg">
                                                No hay stock disponible para transferir
                                            </p>
                                        ) : (
                                            <div className="grid grid-cols-2 gap-2">
                                                {fromLocations.map(stock => (
                                                    <button
                                                        key={stock.locationId}
                                                        type="button"
                                                        onClick={() => {
                                                            setFromLocationId(stock.locationId);
                                                            if (toLocationId === stock.locationId) {
                                                                setToLocationId('');
                                                            }
                                                        }}
                                                        className={`p-3 rounded-xl border-2 text-left transition-all ${fromLocationId === stock.locationId
                                                            ? 'border-red-500 bg-red-50'
                                                            : 'border-gray-200 hover:border-red-300'
                                                            }`}
                                                    >
                                                        <p className="font-medium text-gray-900">{stock.locationName}</p>
                                                        <p className="text-sm text-gray-500">{stock.quantity} {selectedProduct.unit}</p>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* To Location */}
                                    {fromLocationId && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <MapPin className="w-4 h-4 inline mr-1 text-green-500" />
                                                Hacia (Destino)
                                            </label>
                                            <div className="grid grid-cols-2 gap-2">
                                                {toLocations.map(location => (
                                                    <button
                                                        key={location.id}
                                                        type="button"
                                                        onClick={() => setToLocationId(location.id)}
                                                        className={`p-3 rounded-xl border-2 text-left transition-all ${toLocationId === location.id
                                                            ? 'border-green-500 bg-green-50'
                                                            : 'border-gray-200 hover:border-green-300'
                                                            }`}
                                                    >
                                                        <p className="font-medium text-gray-900">{location.name}</p>
                                                        {location.isDefault && (
                                                            <span className="text-xs text-blue-600">Principal</span>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Quantity */}
                                    {fromLocationId && toLocationId && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Cantidad a Transferir
                                            </label>
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="number"
                                                    value={quantity}
                                                    onChange={(e) => setQuantity(e.target.value)}
                                                    placeholder="0"
                                                    min="1"
                                                    max={availableStock}
                                                    className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg text-center"
                                                />
                                                <span className="text-gray-500">{selectedProduct.unit}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setQuantity(availableStock.toString())}
                                                    className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 text-sm"
                                                >
                                                    Todo ({availableStock})
                                                </button>
                                            </div>
                                            <p className="text-xs text-gray-400 mt-1">Disponible en origen: {availableStock}</p>
                                        </div>
                                    )}

                                    {/* Notes */}
                                    {fromLocationId && toLocationId && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Notas (opcional)
                                            </label>
                                            <input
                                                type="text"
                                                value={notes}
                                                onChange={(e) => setNotes(e.target.value)}
                                                placeholder="Ej: Para evento del fin de semana"
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {step === 2 && (
                        <div className="text-center py-6">
                            {/* Summary Visual */}
                            <div className="mb-8">
                                <div className="flex items-center justify-center gap-4">
                                    {/* From */}
                                    <div className="text-center">
                                        <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-2">
                                            <MapPin className="w-8 h-8 text-red-600" />
                                        </div>
                                        <p className="font-semibold text-gray-900">{fromLocation?.name}</p>
                                        <p className="text-sm text-gray-500">Origen</p>
                                    </div>

                                    {/* Arrow with quantity */}
                                    <div className="flex flex-col items-center">
                                        <div className="px-4 py-2 bg-green-100 rounded-full mb-2">
                                            <span className="text-lg font-bold text-green-700">{quantity}</span>
                                        </div>
                                        <ArrowRightLeft className="w-8 h-8 text-green-600" />
                                    </div>

                                    {/* To */}
                                    <div className="text-center">
                                        <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-2">
                                            <MapPin className="w-8 h-8 text-green-600" />
                                        </div>
                                        <p className="font-semibold text-gray-900">{toLocation?.name}</p>
                                        <p className="text-sm text-gray-500">Destino</p>
                                    </div>
                                </div>
                            </div>

                            {/* Product Info */}
                            <div className="p-4 bg-gray-50 rounded-xl mb-6">
                                <div className="flex items-center justify-center gap-3">
                                    <Package className="w-6 h-6 text-blue-600" />
                                    <div>
                                        <p className="font-semibold text-gray-900">{selectedProduct?.name}</p>
                                        <p className="text-sm text-gray-500">SKU: {selectedProduct?.sku}</p>
                                    </div>
                                </div>
                            </div>

                            {notes && (
                                <p className="text-sm text-gray-500 italic mb-6">
                                    Nota: {notes}
                                </p>
                            )}

                            <p className="text-gray-600 mb-4">
                                ¿Confirmas esta transferencia?
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
                    {step === 1 ? (
                        <>
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleNext}
                                disabled={!selectedProductId || !fromLocationId || !toLocationId || !quantity}
                                className="px-6 py-2 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg hover:from-green-700 hover:to-teal-700 transition-all disabled:opacity-50 flex items-center gap-2"
                            >
                                Siguiente
                                <span>→</span>
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                disabled={isLoading}
                            >
                                ← Atrás
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={isLoading}
                                className="px-6 py-2 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg hover:from-green-700 hover:to-teal-700 transition-all disabled:opacity-50 flex items-center gap-2"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        Transfiriendo...
                                    </>
                                ) : (
                                    <>
                                        <ArrowRightLeft className="w-4 h-4" />
                                        Confirmar Transferencia
                                    </>
                                )}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TransferModal;
